import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import Navigation from '@/components/Navigation';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function Settings() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [formData, setFormData] = useState({ name: '' });
  const [loading, setLoading] = useState(false);
  const [darkMode, setDarkMode] = useState(() => {
    return localStorage.getItem('darkMode') === 'true';
  });
  const [privacySettings, setPrivacySettings] = useState({
    passcodeEnabled: false,
    biometricEnabled: false
  });

  useEffect(() => {
    fetchUser();
    // Apply dark mode on mount
    if (darkMode) {
      document.documentElement.classList.add('dark');
    }
  }, []);

  useEffect(() => {
    // Toggle dark mode class on change
    if (darkMode) {
      document.documentElement.classList.add('dark');
      localStorage.setItem('darkMode', 'true');
    } else {
      document.documentElement.classList.remove('dark');
      localStorage.setItem('darkMode', 'false');
    }
  }, [darkMode]);

  const fetchUser = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/auth/me`, {
        credentials: 'include'
      });

      if (!response.ok) throw new Error('Failed to fetch user');

      const data = await response.json();
      setUser(data);
      setFormData({ name: data.name });
    } catch (error) {
      console.error('Error fetching user:', error);
    }
  };

  const handleUpdateProfile = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await fetch(`${BACKEND_URL}/api/profile`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(formData)
      });

      if (!response.ok) throw new Error('Failed to update profile');

      toast.success('Profile updated successfully');
      fetchUser();
    } catch (error) {
      toast.error(error.message || 'Failed to update profile');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    try {
      await fetch(`${BACKEND_URL}/api/auth/logout`, {
        method: 'POST',
        credentials: 'include'
      });

      toast.success('Logged out successfully');
      navigate('/login');
    } catch (error) {
      toast.error('Failed to logout');
    }
  };

  const handleClearActivity = () => {
    // Clear session storage (celebration flags, etc.)
    sessionStorage.clear();
    toast.success('Recent activity cleared');
  };

  const handleAppNameChange = () => {
    toast.info('App name/icon customization is available through your device settings. Add this app to your home screen to customize the display name.');
  };

  const togglePasscode = () => {
    if (!privacySettings.passcodeEnabled) {
      toast.info('Passcode protection will be available in a future update. For now, use your device\'s built-in app lock features.');
    }
    setPrivacySettings({ ...privacySettings, passcodeEnabled: !privacySettings.passcodeEnabled });
  };

  const toggleBiometric = () => {
    if (!privacySettings.biometricEnabled) {
      toast.info('Biometric lock will be available in a future update. For now, use your device\'s built-in security features.');
    }
    setPrivacySettings({ ...privacySettings, biometricEnabled: !privacySettings.biometricEnabled });
  };

  const toggleDarkMode = () => {
    setDarkMode(!darkMode);
    toast.success(darkMode ? 'Light mode enabled' : 'Dark mode enabled');
  };

  return (
    <div className="min-h-screen bg-[#1C2127] pb-20 transition-colors duration-300">
      <Navigation />
      
      <main className="max-w-md mx-auto px-4 py-6 space-y-6 animate-fade-in">
        <div className="space-y-2">
          <h1 className="text-3xl font-bold tracking-tight text-[#F8F9FA]" data-testid="settings-title">
            Settings
          </h1>
          <p className="text-base text-[#ADB5BD] leading-relaxed">
            Manage your account and preferences
          </p>
        </div>

        {/* Appearance Settings */}
        <div className="bg-[#2D3640] rounded-2xl border border-[#4A5568] card-shadow-soft p-6 space-y-4">
          <h2 className="text-xl font-semibold text-[#F8F9FA]">
            Appearance
          </h2>
          
          <div className="p-4 rounded-xl border border-[#4A5568]">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                {darkMode ? (
                  <svg className="w-5 h-5 text-[#00B4D8]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z" />
                  </svg>
                ) : (
                  <svg className="w-5 h-5 text-[#00B4D8]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z" />
                  </svg>
                )}
                <div>
                  <p className="text-sm font-medium text-[#F8F9FA]">Dark Mode</p>
                  <p className="text-xs text-[#ADB5BD]">Calming dark theme for nighttime</p>
                </div>
              </div>
              <button
                onClick={toggleDarkMode}
                className={`w-12 h-6 rounded-full transition-colors ${
                  darkMode ? 'bg-[#00B4D8]' : 'bg-[var(--therapy-text-light)]'
                }`}
                data-testid="dark-mode-toggle"
              >
                <div className={`w-5 h-5 bg-white rounded-full shadow transition-transform ${
                  darkMode ? 'translate-x-6' : 'translate-x-0.5'
                }`} />
              </button>
            </div>
          </div>
        </div>

        {/* Profile Settings */}
        <div className="bg-[#2D3640] rounded-2xl border border-[#4A5568] card-shadow-soft p-6 space-y-4">
          <h2 className="text-xl font-semibold text-[#F8F9FA]">
            Profile Information
          </h2>
          
          <form onSubmit={handleUpdateProfile} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="name" className="text-sm font-medium text-[#F8F9FA]">
                Name
              </Label>
              <Input
                id="name"
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className="h-12 rounded-xl border-[#4A5568] focus:border-[#00B4D8] focus:ring-[#00B4D8]/20 bg-[#2D3640]"
                data-testid="settings-name-input"
              />
            </div>

            <div className="space-y-2">
              <Label className="text-sm font-medium text-[#F8F9FA]">
                Email
              </Label>
              <Input
                type="email"
                value={user?.email || ''}
                disabled
                className="h-12 rounded-xl border-[#4A5568] bg-[#1C2127]"
                data-testid="settings-email-display"
              />
              <p className="text-xs text-[#6B7280]">Email cannot be changed</p>
            </div>

            <Button
              type="submit"
              disabled={loading}
              className="button-primary w-full rounded-full px-6 py-5 h-auto text-base font-medium"
              data-testid="settings-save-btn"
            >
              {loading ? 'Saving...' : 'Save Changes'}
            </Button>
          </form>
        </div>

        {/* Privacy Settings */}
        <div className="bg-[#2D3640] rounded-2xl border border-[#4A5568] card-shadow-soft p-6 space-y-4">
          <h2 className="text-xl font-semibold text-[#F8F9FA]">
            Privacy & Security
          </h2>
          
          <div className="space-y-3">
            <div className="p-4 bg-[#1C2127] rounded-xl">
              <div className="flex items-start gap-3">
                <svg className="w-5 h-5 text-[#00B4D8] flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                </svg>
                <div className="flex-1">
                  <p className="text-sm font-medium text-[#F8F9FA]">Your data is encrypted</p>
                  <p className="text-xs text-[#ADB5BD] mt-1">
                    All your personal information and check-ins are securely stored
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Privacy & Discretion */}
        <div className="bg-[#2D3640] rounded-2xl border border-[#4A5568] card-shadow-soft p-6 space-y-4">
          <div className="space-y-1">
            <h2 className="text-xl font-semibold text-[#F8F9FA]">
              Privacy & Discretion
            </h2>
            <p className="text-sm text-[#ADB5BD]">
              Features to protect your privacy
            </p>
          </div>
          
          <div className="space-y-3">
            {/* App Name/Icon */}
            <button
              onClick={handleAppNameChange}
              className="w-full text-left p-4 rounded-xl border border-[#4A5568] hover:border-[#00B4D8] hover:bg-[#1C2127] transition-all"
              data-testid="app-name-btn"
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <svg className="w-5 h-5 text-[#00B4D8]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z" />
                  </svg>
                  <div>
                    <p className="text-sm font-medium text-[#F8F9FA]">Customize app name/icon</p>
                    <p className="text-xs text-[#ADB5BD]">For discretion on home screen</p>
                  </div>
                </div>
                <svg className="w-5 h-5 text-[#6B7280]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </div>
            </button>

            {/* Passcode */}
            <div className="p-4 rounded-xl border border-[#4A5568]">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <svg className="w-5 h-5 text-[#00B4D8]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z" />
                  </svg>
                  <div>
                    <p className="text-sm font-medium text-[#F8F9FA]">Passcode lock</p>
                    <p className="text-xs text-[#ADB5BD]">Require passcode to open app</p>
                  </div>
                </div>
                <button
                  onClick={togglePasscode}
                  className={`w-12 h-6 rounded-full transition-colors ${
                    privacySettings.passcodeEnabled ? 'bg-[#00B4D8]' : 'bg-[var(--therapy-text-light)]'
                  }`}
                  data-testid="passcode-toggle"
                >
                  <div className={`w-5 h-5 bg-white rounded-full shadow transition-transform ${
                    privacySettings.passcodeEnabled ? 'translate-x-6' : 'translate-x-0.5'
                  }`} />
                </button>
              </div>
            </div>

            {/* Biometric */}
            <div className="p-4 rounded-xl border border-[#4A5568]">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <svg className="w-5 h-5 text-[#00B4D8]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 11c0 3.517-1.009 6.799-2.753 9.571m-3.44-2.04l.054-.09A13.916 13.916 0 008 11a4 4 0 118 0c0 1.017-.07 2.019-.203 3m-2.118 6.844A21.88 21.88 0 0015.171 17m3.839 1.132c.645-2.266.99-4.659.99-7.132A8 8 0 008 4.07M3 15.364c.64-1.319 1-2.8 1-4.364 0-1.457.39-2.823 1.07-4" />
                  </svg>
                  <div>
                    <p className="text-sm font-medium text-[#F8F9FA]">Biometric lock</p>
                    <p className="text-xs text-[#ADB5BD]">Face ID / Fingerprint</p>
                  </div>
                </div>
                <button
                  onClick={toggleBiometric}
                  className={`w-12 h-6 rounded-full transition-colors ${
                    privacySettings.biometricEnabled ? 'bg-[#00B4D8]' : 'bg-[var(--therapy-text-light)]'
                  }`}
                  data-testid="biometric-toggle"
                >
                  <div className={`w-5 h-5 bg-white rounded-full shadow transition-transform ${
                    privacySettings.biometricEnabled ? 'translate-x-6' : 'translate-x-0.5'
                  }`} />
                </button>
              </div>
            </div>

            {/* Clear Activity */}
            <button
              onClick={handleClearActivity}
              className="w-full text-left p-4 rounded-xl border border-[#4A5568] hover:border-[#00B4D8] hover:bg-[#1C2127] transition-all"
              data-testid="clear-activity-btn"
            >
              <div className="flex items-center gap-3">
                <svg className="w-5 h-5 text-[#00B4D8]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                </svg>
                <div>
                  <p className="text-sm font-medium text-[#F8F9FA]">Clear recent activity</p>
                  <p className="text-xs text-[#ADB5BD]">Remove session data for privacy</p>
                </div>
              </div>
            </button>

            <div className="pt-2">
              <p className="text-xs text-[#6B7280] italic">
                These features help protect your privacy if you need discretion
              </p>
            </div>
          </div>
        </div>

        {/* Account Actions */}
        <div className="bg-[#2D3640] rounded-2xl border border-[#4A5568] card-shadow-soft p-6 space-y-4">
          <h2 className="text-xl font-semibold text-[#F8F9FA]">
            Account
          </h2>
          
          <Button
            onClick={handleLogout}
            variant="outline"
            className="w-full rounded-full px-6 py-5 h-auto text-base font-medium border-[#EF476F] text-[#EF476F] hover:bg-[#EF476F]/10"
            data-testid="settings-logout-btn"
          >
            Log Out
          </Button>
        </div>
      </main>
    </div>
  );
}
